import { useState, useMemo } from 'react';

import { Major, AppIcon, CareerPath } from '@backrs_ui/common-ui';

import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';

import { Box, Typography, SxProps, Theme, Chip, Tooltip, ClickAwayListener } from '@mui/material';

import { CareerPathPills } from './CareerPathPills';
import { useAgentContext } from './MajorOverview.agentContext';
import { getHighRelevanceCareerPathsForMajors } from '../majors.utils';

const STRINGS = appStrings.modules.majors.descriptionSidebar;

type MajorOverviewProps = Readonly<{
  major: Major;
  careerPaths: CareerPath[];
}>;

export function MajorOverview({ major, careerPaths }: MajorOverviewProps) {
  const [openTooltip, setOpenTooltip] = useState<{
    typicalCollege: boolean;
    careerPaths: boolean;
    optionValue: boolean;
  }>({
    typicalCollege: false,
    careerPaths: false,
    optionValue: false,
  });

  const highRelevanceCareerPaths = useMemo(
    () => getHighRelevanceCareerPathsForMajors({ major, careerPaths }),
    [major, careerPaths],
  );

  useAgentContext(major, highRelevanceCareerPaths);

  const handleTooltipToggle = (section: keyof typeof openTooltip) => {
    setOpenTooltip((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleTooltipClose = (section: keyof typeof openTooltip) => {
    setOpenTooltip((prev) => ({
      ...prev,
      [section]: false,
    }));
  };

  return (
    <Box sx={containerSx}>
      <Typography variant="text-md-regular" sx={descriptionTextSx}>
        {major.description}
      </Typography>

      <Box sx={sidebarContainerSx}>
        {/* Typical College */}
        <Box sx={sectionSx}>
          <Box sx={labelContainerSx}>
            <Typography variant="text-md-semibold" sx={labelSx}>
              {STRINGS.typicalCollege}
            </Typography>
            <ClickAwayListener onClickAway={() => handleTooltipClose('typicalCollege')}>
              <Tooltip
                open={openTooltip.typicalCollege}
                onClose={() => handleTooltipClose('typicalCollege')}
                title={STRINGS.typicalCollegeInfo}
                placement="top"
                arrow
                disableFocusListener
                disableHoverListener
                disableTouchListener
                componentsProps={{
                  tooltip: {
                    sx: {
                      whiteSpace: 'pre-line',
                      padding: '16px',
                    },
                  },
                }}
              >
                <Box sx={infoBoxSx} onClick={() => handleTooltipToggle('typicalCollege')}>
                  <AppIcon iconName="HelpCircle" diameter="14px" color="Brand/600" />
                </Box>
              </Tooltip>
            </ClickAwayListener>
          </Box>
          <Box sx={chipsContainerSx}>
            {major.typicalCollege ? (
              <Chip
                variant="badge"
                label={major.typicalCollege}
                size="md"
                sx={typicalCollegeChipSx}
                type="leading"
                icon={
                  <Box sx={programIconSx}>
                    <AppIcon iconName="GraduationHat01" diameter="12px" color="Brand/50" />
                  </Box>
                }
              />
            ) : (
              <Typography variant="text-sm-regular" sx={noDataSx}>
                {STRINGS.nA}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Career Paths */}
        <CareerPathPills highRelevanceCareerPaths={highRelevanceCareerPaths} />
      </Box>
    </Box>
  );
}

const containerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-4xl'],
  alignItems: 'flex-start',
});

const descriptionTextSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  flex: '1 1 min(535px, 100%)',
  minWidth: 0,
});

const sidebarContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-2xl'],
  flex: '1 1 280px',
  maxWidth: '280px',
});

const sectionSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-md'],
});

const labelContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: backrs.spacing['spacing-xs'],
});

const labelSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Brand/600'],
});

const chipsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: backrs.spacing['spacing-sm'],
});

const typicalCollegeChipSx: SxProps<Theme> = ({ backrs, typography }) => ({
  backgroundColor: backrs.colors['Colors/Base/White'],
  border: `1px solid ${backrs.colors['Colors/Border/border-primary']}`,
  '& .MuiChip-label': {
    ...typography['text-xs-regular'],
    color: backrs.colors['Gray/700'],
  },
  gap: backrs.spacing['spacing-lg'],
});

const programIconSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  width: '16px',
  height: '16px',
  borderRadius: backrs.radius['radius-full'],
  backgroundColor: backrs.colors['Brand/500'],
});

const noDataSx: SxProps<Theme> = ({ backrs }) => ({
  color: backrs.colors['Colors/Text/text-tertiary'],
  fontStyle: 'italic',
});

const infoBoxSx: SxProps<Theme> = {
  cursor: 'pointer',
};
